import { supabase } from '../lib/supabase'
import { ALL_TIME, DateRange, inRange } from '../lib/dateRange'
import { netMarginOf } from '../lib/margin'
import { CostType, money } from '../lib/commission'
import {
  CostLine as V2CostLine,
  accrualPlan,
  CalcGate,
  canCalculate,
  commissionAmount,
  duplicateInvoiceRefs,
  estimateWayOff,
  estimatedLineCount,
  missingBillCount,
  landedCost,
  netMargin,
  unreversedAccrualValue,
  readBillStatus,
  BillStatus,
  expectedCostTypes,
} from '../lib/commissionV2'
import { categoryForCostType, categoryOf } from '../lib/costCategories'
import { ESTIMATE_SOURCE_STANDARD, isAccrualSource, isOverrideSource } from '../lib/estimateSource'

/**
 * The Commission Report — Winnie's build spec v2.
 *
 * v1's provisional-at-Sold / final-at-Settled model is gone. Section 0 of the spec:
 * *"That model is scrapped. It was based on an assumption that turned out to contradict the
 * accounting team's actual Xero journal design."* Commission is struck once and never moves
 * again; when an estimate turns out wrong the correction goes through the P&L.
 *
 * 9 Sep, later the same day: the lock point is back at Sold, on Nick's instruction to build
 * to the newer document. Build spec v2 says it twice - section 3, "Vehicle status -> Sold (in
 * transit or delivered)", and section 9, "commission calculates exactly once per vehicle, at
 * Sold". The earlier prose about waiting for Settled is v1 thinking, and section 0 scraps it.
 *
 * What makes Sold safe is not timing but section 8. A cost line with no invoice and no
 * standard rate configured blocks the calculation as a config gap; it is never read as $0.
 * A vehicle can reach Sold and still not be struck until its costs are either invoiced or
 * covered by a rate card. That is the guard v1 was trying to buy with a holdback, and it
 * costs nobody a clawback.
 *
 * Two things about this file are worth knowing before reading it.
 *
 * FIRST: it reconciles against the platform's own margin rather than replacing it.
 * `netMarginOf` in margin.ts takes its costs from columns on the deal and subtracts a buying
 * fee; the spec takes its costs from cost lines and has no buying fee. That is a real
 * disagreement about what a margin is. Every row carries both numbers and the variance, and
 * nobody's existing figures move until somebody decides which is canonical. Live, the
 * variance is exactly the buying fee on all 153 sold vehicles — which makes it a question
 * with one answer, not a bug.
 *
 * SECOND: cost lines come from `expenses` where they exist and are synthesised from the
 * deal's own columns where they do not. There is a handful of rows in `expenses` while 153
 * sold deals carry their costs in `valuation_amount`, `capitalised_transport`,
 * `capitalised_repairs` and `transport_cost`. A report reading only `expenses` would show
 * every vehicle at a 100% margin — worse than useless, because it is confidently wrong.
 * Synthesised lines are marked as such so the drill-through says where each number came
 * from, and a real expense row always beats the deal column it would duplicate.
 *
 * The v1 columns on `commission_records` (holdback, true-up deadline, final/adjustment) are
 * left in the database and are never read or written here. Dropping columns is irreversible;
 * leaving them dormant costs nothing and keeps the door open if the model moves again.
 */

/* Money inside a sentence gets the same treatment as money inside a cell: toFixed drops the
   thousands separator, which is how $18500.00 once appeared beside $18,500.00 on one card. */
const aud = (n: number) =>
  `${n < 0 ? '-$' : '$'}${Math.abs(n).toLocaleString('en-AU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`

interface DealRow {
  id: string
  stock_number: string | null
  vehicle_vin: string | null
  autograb_vin: string | null
  vehicle_make: string | null
  vehicle_model: string | null
  autograb_make: string | null
  autograb_model: string | null
  sale_price: number | null
  sold_at: string | null
  sale_date: string | null
  settled_at: string | null
  commission_pct: number | null
  valuation_amount: number | null
  capitalised_transport: number | null
  capitalised_repairs: number | null
  inspection_cost: number | null
  transport_cost: number | null
  transport_arranged_by: string | null
  deal_status: string | null
  deleted_at: string | null
}

interface ExpenseRow {
  id: string
  deal_id: string | null
  amount: number | null
  cost_type: CostType | null
  category: string | null
  description: string | null
  supplier: string | null
  invoice_ref: string | null
  supplier_invoice_id: string | null
  bill_status: string | null
  incurred_on: string | null
  estimated_amount: number | null
  estimate_source: string | null
  estimate_reconciled_at: string | null
  override_reason: string | null
}

export interface ReportCostLine {
  id: string
  costType: CostType | null
  label: string
  amount: number
  supplier: string
  invoiceRef: string
  billStatus: BillStatus
  isAccrual: boolean
  accrualReversed: boolean
  /** Set when a super admin put this figure in by hand: the reason they gave. */
  overrideReason: string | null
  /** What was accrued, kept beside the actual so the variance stays visible. */
  estimatedAmount: number | null
  source: 'expense' | 'deal' | 'planned'
}

export interface CommissionRow {
  dealId: string
  vin: string
  vehicle: string
  salePrice: number | null
  landedCost: number
  netMargin: number | null
  commissionPct: number
  commissionAmount: number | null
  calculatedAt: string | null
  isLocked: boolean
  /** How many of this vehicle's cost lines rest on an estimate rather than an invoice. */
  estimatedLines: number
  /** Cost lines with no invoice AND no estimate behind them - spec section 2. */
  missingBills: number
  unreversedAccruals: number
  /** Whether commission could be struck right now, and why not when it cannot. */
  gate: { ok: boolean; code: string; reason: string }
  /**
   * The cost types holding this vehicle up: no invoice, and no standard rate either.
   *
   * The gate already says the vehicle is blocked; this says which costs to do something
   * about, so a super admin can put a figure against the one that is missing rather than
   * being told "a cost" and left to guess which.
   */
  blockedCosts: CostType[]
  /** The platform's own margin, and the gap to the spec's. */
  platformNetMargin: number | null
  marginVariance: number | null
  marginDisagrees: boolean
  warnings: string[]
  lines: ReportCostLine[]
  soldOn: string | null
}

export interface CommissionReport {
  rows: CommissionRow[]
  summary: {
    /** Struck and locked - money somebody could actually be paid. */
    totalCommission: number
    vehiclesWithAccruals: number
    unreversedAccrualValue: number
  }
  totals: {
    vehicles: number
    disagreeing: number
    blocked: number
    estimatedLines: number
    /** Cost lines across the whole report with neither an invoice nor an estimate. */
    missingBills: number
  }
  defaults: Partial<Record<CostType, number>>
  defaultCommissionPct: number
}

/* --------------------------------------------------------------------------------------
 * Loading
 * ------------------------------------------------------------------------------------ */

const vinOf = (d: DealRow) => d.autograb_vin || d.vehicle_vin || ''
const vehicleOf = (d: DealRow) =>
  [d.autograb_make || d.vehicle_make, d.autograb_model || d.vehicle_model].filter(Boolean).join(' ') ||
  (d.stock_number ? `#${d.stock_number}` : d.id.slice(0, 8))

/**
 * Is this vehicle at the lock point?
 *
 * Spec section 3: the trigger is status -> Sold, in transit or delivered. `settled_at` counts
 * too, because a vehicle cannot have settled without having been sold. In v2 this is both the
 * inclusion test for the report and the gate on striking commission - they are one moment.
 */
function isSold(d: DealRow): boolean {
  if (d.settled_at || d.sold_at) return true
  const s = String(d.deal_status || '').toLowerCase()
  return s.includes('sold') || s === 'awaiting_payment' || s === 'closed'
}

/**
 * The v2 cost type a cost line counts as.
 *
 * An explicit `cost_type` on the row wins. Otherwise the category decides, and that mapping
 * lives in `costCategories.ts` beside the categories themselves rather than in a second copy
 * here that can drift out of step with it.
 *
 * `transport` deliberately maps to nothing. Freight in and freight out sit on opposite sides
 * of the margin and the word does not say which, so the person entering it is asked rather
 * than guessed at - the split-invoice form and the Add Expense form both put the question.
 */
function costTypeOf(e: ExpenseRow): CostType | null {
  if (e.cost_type) return e.cost_type
  return categoryOf(e.category)?.costType || null
}

function costLinesFor(deal: DealRow, expenses: ExpenseRow[]): ReportCostLine[] {
  const real: ReportCostLine[] = expenses.map((e) => {
    const ct = costTypeOf(e)
    /* Read the column, do not collapse it. This was a two-way ternary, so a cost line the
       database calls Missing - no invoice, no estimate - was reported as Received, which is
       the one status that means the opposite. Winnie asks for all three in section 2. */
    const status = readBillStatus(e.bill_status)
    return {
      id: e.id,
      costType: ct,
      label: e.description || e.category || 'Cost',
      amount: money(Number(e.amount) || 0),
      supplier: e.supplier || '',
      invoiceRef: e.invoice_ref || '',
      billStatus: status,
      isAccrual: isAccrualSource(e.estimate_source),
      accrualReversed: Boolean(e.estimate_reconciled_at),
      estimatedAmount: e.estimated_amount === null ? null : Number(e.estimated_amount),
      /* A figure a super admin typed rests on them rather than on a rate, so the report
         carries the reason instead of letting it read like any other accrual. */
      overrideReason: isOverrideSource(e.estimate_source) ? e.override_reason || null : null,
      source: 'expense' as const,
    }
  })

  const covered = new Set(real.map((l) => l.costType).filter(Boolean) as CostType[])
  const out = real.slice()

  const add = (ct: CostType, label: string, amount: number | null) => {
    if (covered.has(ct)) return
    const v = Number(amount) || 0
    if (!v) return
    out.push({
      id: `${deal.id}:${ct}`,
      costType: ct,
      label,
      amount: money(v),
      supplier: '',
      invoiceRef: '',
      billStatus: 'Received',
      isAccrual: false,
      accrualReversed: false,
      estimatedAmount: null,
      overrideReason: null,
      source: 'deal',
    })
  }

  add('PurchasePrice', 'Purchase price (from the deal)', deal.valuation_amount)
  add('FreightIn', 'Freight in (capitalised transport)', deal.capitalised_transport)
  add('Reconditioning', 'Reconditioning (capitalised repairs)', deal.capitalised_repairs)
  add('Inspection', 'Inspection (from the deal)', deal.inspection_cost)
  /* margin.ts only counts outbound transport when Direct Auto is carrying it; the same test
     has to apply here or the two figures disagree for a reason that is not real. */
  if (deal.transport_arranged_by !== 'dealer') {
    add('FreightOut', 'Freight out (delivery)', deal.transport_cost)
  }

  return out
}

/**
 * The standard rates, keyed by cost type, for the accrual plan.
 *
 * Only flat rates are usable here — a distance-banded rate needs a distance, which belongs to
 * one vehicle rather than to the report as a whole. `standardCostService.accrualCandidates`
 * does the per-vehicle version properly; this is the report's coarse view of what is
 * configured at all, which is what section 8's "config gap" check needs.
 */
async function loadDefaults(): Promise<Partial<Record<CostType, number>>> {
  try {
    const { data } = await supabase
      .from('standard_costs')
      .select('cost_type, basis, amount, active')
      .eq('active', true)
      .eq('basis', 'flat')
    const out: Partial<Record<CostType, number>> = {}
    for (const r of (data || []) as unknown as { cost_type: CostType; amount: number }[]) {
      out[r.cost_type] = Number(r.amount)
    }
    return out
  } catch {
    /* The table does not exist until the migration is run. No rates means every accrual is a
       config gap, which is exactly what the spec wants surfaced. */
    return {}
  }
}

async function loadDefaultPct(): Promise<number> {
  try {
    const { data } = await supabase
      .from('commission_assumptions')
      .select('default_commission_pct')
      .eq('id', 1)
      .maybeSingle()
    const p = Number((data as { default_commission_pct?: number } | null)?.default_commission_pct)
    return Number.isFinite(p) && p > 0 ? p : 0.2
  } catch {
    return 0.2
  }
}

/* --------------------------------------------------------------------------------------
 * The report
 * ------------------------------------------------------------------------------------ */

export async function loadCommissionReport(range: DateRange = ALL_TIME): Promise<CommissionReport> {
  const [d, e, defaults, defaultPct, recs] = await Promise.all([
    supabase.from('deals').select('*').is('deleted_at', null),
    supabase.from('expenses').select('*'),
    loadDefaults(),
    loadDefaultPct(),
    /* Active records only. A superseded row is the figure somebody was paid on before the
       deal terms changed, and a voided row belongs to a sale that was cancelled - neither is
       the commission in force, and counting either would double up the report's totals. */
    supabase.from('commission_records').select('*').is('superseded_at', null).is('voided_at', null),
  ])
  if (d.error) throw d.error
  if (e.error) throw e.error

  const deals = (d.data || []) as unknown as DealRow[]
  const expenses = (e.data || []) as unknown as ExpenseRow[]
  const records = (recs.data || []) as unknown as Record<string, unknown>[]

  const byDeal: Record<string, ExpenseRow[]> = {}
  for (const x of expenses) {
    if (!x.deal_id) continue
    ;(byDeal[x.deal_id] = byDeal[x.deal_id] || []).push(x)
  }
  const recByDeal: Record<string, Record<string, unknown>> = {}
  for (const r of records) recByDeal[String(r.deal_id)] = r

  const rows: CommissionRow[] = []

  for (const deal of deals) {
    if (!isSold(deal)) continue

    /* A range on this report means the period the vehicle sold in — that is when commission
       is struck, so it is the date the report is about. */
    const soldWhen = (deal.sold_at || deal.sale_date || '').slice(0, 10) || null
    if (soldWhen && !inRange(soldWhen, range)) continue

    const lines = costLinesFor(deal, byDeal[deal.id] || [])
    const v2Lines: V2CostLine[] = lines.map((l) => ({
      id: l.id,
      cost_type: l.costType,
      amount: l.amount,
      bill_status: l.billStatus,
      invoice_ref: l.invoiceRef,
      is_accrual: l.isAccrual,
      accrual_reversed: l.accrualReversed,
    }))

    const rec = recByDeal[deal.id]
    const locked = Boolean(rec && rec.locked_at)

    const plan = accrualPlan(v2Lines, defaults, expectedCostTypes({ transportArrangedBy: deal.transport_arranged_by }))
    const gate: CalcGate = canCalculate({ salePrice: deal.sale_price, plan, alreadyLocked: locked })

    /* An accrual that WOULD be written at the lock point is part of what commission will be
       struck on, so it belongs in the figure on screen. Leaving it out would mean the landed
       cost silently jumps the moment somebody presses Calculate - and jumps in the direction
       that flatters the margin, which is the worst direction for a number to move on its own.
       These lines are marked `planned` so the drill-through says they are not posted yet. */
    if (!locked) {
      for (const p of plan.lines) {
        if (p.action !== 'accrue' || p.amount === null) continue
        if (v2Lines.some((l) => l.cost_type === p.costType)) continue
        lines.push({
          id: `${deal.id}:planned:${p.costType}`,
          costType: p.costType,
          label: 'Standard rate - not posted yet',
          amount: p.amount,
          supplier: '',
          invoiceRef: '',
          billStatus: 'Estimated',
          isAccrual: true,
          accrualReversed: false,
          estimatedAmount: p.amount,
          overrideReason: null,
          source: 'planned',
        })
        v2Lines.push({
          cost_type: p.costType,
          amount: p.amount,
          bill_status: 'Estimated',
          is_accrual: true,
          accrual_reversed: false,
        })
      }
    }

    const pct = Number(deal.commission_pct) > 0 ? Number(deal.commission_pct) : defaultPct

    /* A locked record reads back its stored figures rather than recomputing. That is the whole
       point of the lock: a commission struck in September has to read the same next June. */
    const cost = locked ? money(Number(rec.landed_cost) || 0) : landedCost(v2Lines)
    const margin = locked
      ? (rec.net_margin === null ? null : money(Number(rec.net_margin)))
      : netMargin(deal.sale_price, v2Lines)
    const commission = locked
      ? (rec.commission_amount === null ? null : money(Number(rec.commission_amount)))
      : commissionAmount(margin, pct)

    const platform = netMarginOf(deal as unknown as Parameters<typeof netMarginOf>[0])
    const variance = margin === null || platform === null ? null : money(margin - platform)

    const warnings: string[] = []
    if (!gate.ok && gate.code !== 'already_locked') warnings.push(gate.reason)
    for (const dup of duplicateInvoiceRefs(v2Lines)) {
      warnings.push(`Invoice ${dup} appears twice on this vehicle. Check it is not the same bill entered twice.`)
    }
    for (const l of lines) {
      if (l.estimatedAmount !== null && l.billStatus === 'Received' && estimateWayOff(l.estimatedAmount, l.amount)) {
        warnings.push(
          `${l.label} was accrued at ${aud(l.estimatedAmount)} and invoiced at ${aud(l.amount)}. The commission does not change — but the standard rate probably should.`,
        )
      }
    }
    const missing = missingBillCount(v2Lines)
    if (missing > 0) {
      warnings.push(
        missing +
          (missing === 1 ? ' cost line on this vehicle has' : ' cost lines on this vehicle have') +
          ' no supplier invoice and no estimate behind them. The landed cost below is missing whatever they turn out to be.',
      )
    }

    if (margin !== null && margin < 0) {
      warnings.push('This vehicle sold for less than it cost, so the commission on it is negative.')
    }

    rows.push({
      dealId: deal.id,
      vin: vinOf(deal),
      vehicle: vehicleOf(deal),
      salePrice: deal.sale_price === null ? null : money(Number(deal.sale_price)),
      landedCost: cost,
      netMargin: margin,
      commissionPct: pct,
      commissionAmount: commission,
      calculatedAt: locked ? String(rec.calculated_at || rec.locked_at || '') : null,
      isLocked: locked,
      estimatedLines: estimatedLineCount(v2Lines),
      missingBills: missingBillCount(v2Lines),
      unreversedAccruals: unreversedAccrualValue(v2Lines),
      gate,
      /* PurchasePrice is left in deliberately: the screen has to be able to say that this
         one is not something to type a number into, rather than quietly omitting it. */
      blockedCosts: plan.configGaps.slice(),
      platformNetMargin: platform === null ? null : money(platform),
      marginVariance: variance,
      marginDisagrees: variance !== null && Math.abs(variance) >= 0.01,
      warnings,
      lines,
      soldOn: soldWhen,
    })
  }

  /* Locked first — those are the figures somebody has been paid against — then blocked, then
     the rest. The two ends of the list are the two things a person opens this report for. */
  rows.sort((a, b) => {
    const rank = (r: CommissionRow) => (r.isLocked ? 0 : r.gate.ok ? 2 : 1)
    const diff = rank(a) - rank(b)
    if (diff !== 0) return diff
    return (b.soldOn || '').localeCompare(a.soldOn || '')
  })

  const summary = {
    /* Only what has actually been struck. Section 7 asks for "total commission calculated",
       and a figure computed on a row that section 8 refuses to calculate is not a calculation
       - it is arithmetic on an incomplete cost picture. Today that is $0.00, and the blocked
       count beside it says why. */
    totalCommission: money(
      rows.filter((r) => r.isLocked).reduce((s, r) => s + (r.commissionAmount || 0), 0),
    ),
    vehiclesWithAccruals: rows.filter((r) => r.estimatedLines > 0).length,
    unreversedAccrualValue: money(rows.reduce((s, r) => s + r.unreversedAccruals, 0)),
  }

  return {
    rows,
    summary,
    totals: {
      vehicles: rows.length,
      disagreeing: rows.filter((r) => r.marginDisagrees).length,
      /* A blocked row is one section 8 refuses to calculate: a cost with neither an invoice
         nor a standard rate, or no sale price at all. `already_locked` is not a blockage -
         it is the finished state. */
      blocked: rows.filter((r) => !r.gate.ok && r.gate.code !== 'already_locked').length,
      estimatedLines: rows.reduce((s, r) => s + r.estimatedLines, 0),
      missingBills: rows.reduce((s, r) => s + r.missingBills, 0),
    },
    defaults,
    defaultCommissionPct: defaultPct,
  }
}

/* --------------------------------------------------------------------------------------
 * Striking commission — once
 * ------------------------------------------------------------------------------------ */

export interface CalculateResult {
  ok: boolean
  code: string
  message: string
}

/**
 * Strike commission on one vehicle and lock it.
 *
 * Every refusal in `canCalculate` is enforced here before anything is written, and the write
 * itself is an insert rather than an upsert: a vehicle that already has a commission record
 * gets a refusal, not a second figure. Spec section 9 asks that `is_locked` is never set back
 * to false by any code path, and the simplest way to keep that promise is to have no code
 * path that writes it twice.
 *
 * The v1 columns are deliberately absent from the row this writes. They still exist on the
 * table and they stay null.
 */
export async function calculateCommission(dealId: string): Promise<CalculateResult> {
  const [defaults, defaultPct] = await Promise.all([loadDefaults(), loadDefaultPct()])

  const [d, e, rec] = await Promise.all([
    supabase.from('deals').select('*').eq('id', dealId).maybeSingle(),
    supabase.from('expenses').select('*').eq('deal_id', dealId),
    supabase
      .from('commission_records')
      .select('*')
      .eq('deal_id', dealId)
      .is('superseded_at', null)
      .is('voided_at', null)
      .maybeSingle(),
  ])
  if (d.error) throw d.error
  if (e.error) throw e.error
  if (!d.data) return { ok: false, code: 'not_found', message: 'That vehicle could not be loaded.' }

  const deal = d.data as unknown as DealRow
  if (!isSold(deal)) {
    return {
      ok: false,
      code: 'not_sold',
      message:
        'Commission is struck when the vehicle is sold - in transit or delivered. This one has not reached that point yet.',
    }
  }

  const lines = costLinesFor(deal, (e.data || []) as unknown as ExpenseRow[])
  const v2Lines: V2CostLine[] = lines.map((l) => ({
    cost_type: l.costType,
    amount: l.amount,
    bill_status: l.billStatus,
    invoice_ref: l.invoiceRef,
    is_accrual: l.isAccrual,
    accrual_reversed: l.accrualReversed,
  }))

  const locked = Boolean(rec.data && (rec.data as { locked_at?: string | null }).locked_at)
  const plan = accrualPlan(v2Lines, defaults, expectedCostTypes({ transportArrangedBy: deal.transport_arranged_by }))
  const gate = canCalculate({ salePrice: deal.sale_price, plan, alreadyLocked: locked })
  if (!gate.ok) return { ok: false, code: gate.code, message: gate.reason }

  const pct = Number(deal.commission_pct) > 0 ? Number(deal.commission_pct) : defaultPct
  const now = new Date().toISOString()

  /* Spec section 3: at the lock point, a cost type with no invoice is AUTO-CREATED as an
     estimated cost line before the calculation runs.

     This has to happen before the arithmetic, and the arithmetic has to read the result.
     Gating on the plan but then computing off the pre-accrual lines would understate the
     landed cost by exactly the accrued amount - overstating the margin, and overstating a
     commission that this model then locks forever. The accruals are written FIRST for the
     same reason: if the commission insert fails afterwards the costs still exist and the
     vehicle can be calculated again, whereas a commission locked against costs that were
     never written is not recoverable by anything short of a person with SQL access. */
  const toAccrue = plan.lines.filter(
    (p) => p.action === 'accrue' && p.amount !== null && !v2Lines.some((l) => l.cost_type === p.costType),
  )
  if (toAccrue.length) {
    const { error: accErr } = await supabase.from('expenses').insert(
      toAccrue.map((p) => ({
        deal_id: dealId,
        cost_type: p.costType,
        category: categoryForCostType(p.costType),
        amount: p.amount,
        estimated_amount: p.amount,
        estimate_source: ESTIMATE_SOURCE_STANDARD,
        estimate_applied_at: now,
        bill_status: 'Estimated',
        description: 'Estimated - unconfirmed. Accrued at the standard rate when commission was struck.',
        incurred_on: now.slice(0, 10),
      })),
    )
    if (accErr) throw accErr
    for (const p of toAccrue) {
      v2Lines.push({
        cost_type: p.costType,
        amount: p.amount,
        bill_status: 'Estimated',
        is_accrual: true,
        accrual_reversed: false,
      })
    }
  }

  const cost = landedCost(v2Lines)
  const margin = netMargin(deal.sale_price, v2Lines)
  const amount = commissionAmount(margin, pct)

  /* Spec v2 section 1, on the columns the spec names. The v1 `provisional_`/`final_`/
     `holdback_`/`adjustment_` columns and the Provisional|Final|Adjusted status enum were
     dropped on 9 Sep - section 0: "remove them. They don't exist in this model." */
  const { error } = await supabase.from('commission_records').insert([
    {
      deal_id: dealId,
      commission_pct: pct,
      landed_cost: cost,
      net_margin: margin,
      commission_amount: amount,
      calculated_at: now,
      locked_at: now,
      updated_at: now,
    },
  ])
  if (error) throw error

  return {
    ok: true,
    code: 'calculated',
    message:
      `Commission of ${aud(amount || 0)} on a margin of ${aud(margin || 0)}.` +
      (toAccrue.length
        ? ` ${toAccrue.length} cost${toAccrue.length === 1 ? ' was' : 's were'} accrued at the standard rate and are included in that figure.`
        : '') +
      ' Locked — a later invoice goes through the P&L, not back through this figure.',
  }
}

/* --------------------------------------------------------------------------------------
 * CSV
 * ------------------------------------------------------------------------------------ */

const csvCell = (v: unknown) => {
  const s = v === null || v === undefined ? '' : String(v)
  /* A leading =, +, - or @ is executed as a formula by Excel. Prefixing an apostrophe is the
     standard defence and costs nothing on a value nobody is going to sum in the CSV itself. */
  const safe = /^[=+\-@]/.test(s) ? `'${s}` : s
  return /[",\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe
}

export function commissionReportCsv(rows: CommissionRow[]): string {
  const head = [
    'VIN',
    'Vehicle',
    'Sale price',
    'Landed cost',
    'Net margin',
    'Commission %',
    'Commission amount',
    'Calculated',
    'Estimated cost lines',
    'Missing bills',
    'Unreversed accruals',
    'Status',
    'Platform net margin',
    'Variance',
  ]
  const body = rows.map((r) =>
    [
      r.vin,
      r.vehicle,
      r.salePrice,
      r.landedCost,
      r.netMargin,
      r.commissionPct,
      r.commissionAmount,
      r.calculatedAt ? r.calculatedAt.slice(0, 10) : '',
      r.estimatedLines,
      r.missingBills,
      r.unreversedAccruals,
      r.isLocked ? 'Locked' : r.gate.ok ? 'Ready' : 'Blocked',
      r.platformNetMargin,
      r.marginVariance,
    ]
      .map(csvCell)
      .join(','),
  )
  return [head.map(csvCell).join(','), ...body].join('\n')
}

/* ==========================================================================
 * Recalculating after the DEAL TERMS changed — not after a cost varied.
 *
 * Winnie, 18 September, on both sale amendments and field corrections: "This is a different
 * case from the commission spec's 'never recalculate' rule — don't apply that rule here...
 * A correction means the recorded sale terms were wrong from the start; the true figure is
 * different, so anything calculated off the wrong figure needs to reflect the right one."
 *
 * Commission spec v2 section 4 says the opposite, and a database trigger enforces it. Both
 * rules hold at once, because the recalculation here NEVER EDITS THE STRUCK RECORD. The
 * original is marked superseded and keeps every figure it was struck with — the number
 * somebody was actually paid against stays readable forever — and a fresh record carries the
 * new terms. `superseded_at` is not in the trigger's guarded list, so marking it is allowed
 * without weakening the guard on the money.
 *
 * That is the whole design decision, and it is why `status = 'Recalculated'` was not added:
 * the v2 spec removed the status enum on purpose, and a superseding row records more than a
 * status ever could — both figures, both moments, and the reason in between.
 * ======================================================================= */

export interface RecalcResult {
  ok: boolean
  message: string
  /** False when there was no commission struck yet, which is not an error. */
  superseded: boolean
}

export async function recalculateAfterTermsChange(
  dealId: string,
  reason: string,
  userId?: string | null,
): Promise<RecalcResult> {
  const { data: current, error } = await supabase
    .from('commission_records')
    .select('id, locked_at, commission_amount')
    .eq('deal_id', dealId)
    .is('superseded_at', null)
    .is('voided_at', null)
    .maybeSingle()
  if (error) throw error

  if (!current) {
    /* Nothing struck yet — the vehicle sells, commission is struck then, off the corrected
       figures. Not a failure and not something to announce as one. */
    return { ok: true, superseded: false, message: 'No commission had been struck yet, so there was nothing to restate.' }
  }

  const previousAmount = Number((current as { commission_amount?: number }).commission_amount) || 0

  const { error: markErr } = await supabase
    .from('commission_records')
    .update({
      superseded_at: new Date().toISOString(),
      supersede_reason: reason,
    })
    .eq('id', (current as { id: string }).id)
  if (markErr) throw markErr

  /* With the old record out of the way, the ordinary calculation runs against the corrected
     deal and strikes a new one. It reads the same costs and the same rules — this path does
     not get its own arithmetic. */
  const struck = await calculateCommission(dealId)
  if (!struck.ok) {
    /* Put the old record back rather than leaving the vehicle with no commission at all. A
       restatement that half-happened is worse than one that did not. */
    await supabase
      .from('commission_records')
      .update({ superseded_at: null, supersede_reason: null })
      .eq('id', (current as { id: string }).id)
    return {
      ok: false,
      superseded: false,
      message:
        'Commission could not be restated (' +
        struck.message +
        ') so the original figure has been left in place. Restate it from the Commission Report once that is sorted.',
    }
  }

  const { data: fresh } = await supabase
    .from('commission_records')
    .select('id, commission_amount')
    .eq('deal_id', dealId)
    .is('superseded_at', null)
    .is('voided_at', null)
    .maybeSingle()

  if (fresh) {
    await supabase
      .from('commission_records')
      .update({ superseded_by: (fresh as { id: string }).id })
      .eq('id', (current as { id: string }).id)
  }
  if (userId) void userId

  const newAmount = Number((fresh as { commission_amount?: number } | null)?.commission_amount) || 0
  return {
    ok: true,
    superseded: true,
    message:
      'Commission restated from ' + aud(previousAmount) + ' to ' + aud(newAmount) + '. The original figure is kept.',
  }
}

/**
 * Void the commission on a sale that was cancelled outright.
 *
 * Winnie: "CommissionRecord.status = Voided — retained for audit, zeroed out of active
 * commission totals, never hard-deleted." Marked, never deleted; the figures on the row are
 * untouched, so what was struck is still readable.
 */
export async function voidCommission(
  dealId: string,
  reason: string,
  userId?: string | null,
): Promise<{ ok: boolean; message: string }> {
  const { data: current, error } = await supabase
    .from('commission_records')
    .select('id, commission_amount')
    .eq('deal_id', dealId)
    .is('superseded_at', null)
    .is('voided_at', null)
    .maybeSingle()
  if (error) throw error
  if (!current) return { ok: true, message: 'There was no commission struck against this sale.' }

  const { error: vErr } = await supabase
    .from('commission_records')
    .update({ voided_at: new Date().toISOString(), voided_reason: reason, voided_by: userId || null })
    .eq('id', (current as { id: string }).id)
  if (vErr) throw vErr

  const amount = Number((current as { commission_amount?: number }).commission_amount) || 0
  return { ok: true, message: 'Commission of ' + aud(amount) + ' voided and taken out of active totals.' }
}
